import React, { Component } from 'react'
import { some, values } from 'lodash'
import siteApi, { SetupStatusResponse } from '../apis/site.api'
import Button from '../components/Button'
import Card from '../components/Card'
import Heading from '../components/Heading'
import Icon from '../components/Icon'
import PageHeading from '../components/PageHeading'
import { i18n } from "../i18n/i18n";
interface Props {}

interface State {
    status: SetupStatusResponse
}

interface Step {
  module: Module,
  heading: string
  description: string
  doc: string
}

type Module = keyof SetupStatusResponse

export default class SiteSetupStatus extends Component<Props, State> {
  initialStatus = {
    category: true,
    item: true,
    location: true
  }

  constructor (props: Props) {
    super(props)
    this.state = {
      status: this.initialStatus
    }
  }

  steps: Step[] = [
    {
      module: 'category',
      heading: 'Create Item Categories',
      description: 'In QPRO+, a category refers the how the goods you produce are identified. Often this can be by a case, roll, sack, or even a barrel, etc.',
      doc: 'categories-create-category'
    },
    {
      module: 'item',
      heading: 'Create Items',
      description: 'Items are defined with item codes, part numbers, or SKUs. In QPRO+, you can apply descriptions and additional details to enable valuable features within the platform.',
      doc: 'items-create-item'
    },
    {
      module: 'location',
      heading: 'Create Locations',
      description: 'The locations you create will identify where your items are produced, stored, and even shipped from. Location can also be a location where you receive or stage raw material. QPRO+ will enable you to transfer goods and materials to and from these locations.',
      doc: 'locations-create-location'
    }
  ]

  get visible () {
    const statuses = values(this.state.status)
    return some(statuses, status => !status)
  }

  get className () {
    const base = 'bg-[rgba(0,0,0,0.2)] w-full h-full absolute inset-0 z-50'
    const display = this.visible ? 'block' : 'hidden'
    return `${base} ${display}`
  }

  get activeSteps () {
    return this.steps.filter(step => !this.state.status[step.module])
  }

  step = (step: Step, index: number) => {
    const visible = !this.state.status[step.module]
    const heading = `${index + 1}. ${step.heading}`
    return visible &&
      <div className='my-5' key={step.module}>
        <PageHeading
          text={heading}
          doc={step.doc}
          className='mb-1'/>
        <p className='text-gray-700'>{step.description}</p>
      </div>
  }

  resetStatus () {
    this.setState({ status: this.initialStatus })
  }

  updateStatus () {
    siteApi.setupStatus((status) => {
      if (!status) return
      this.setState({ status })
    })
  }

  handleCloseClick = () => {
    this.resetStatus()
  }

  handleStartVideoClick = () => {
    const url = '/'
    window.open(url, '_blank')
  }

  handleDemoClick = () => {
    const url = 'https://calendly.com/quandopro'
    window.open(url, '_blank')
  }

  componentDidMount () {
    this.updateStatus()
  }

  render () {
    return this.visible &&
      <div className={this.className}>
        <Card className='w-11/12 md:w-1/2 xl:w-1/3 mx-auto mt-10 m-5 max-h-[calc(100vh-60px)] overflow-x-scroll relative'>
            <Heading text='Welcome to QPRO+' />
            <Heading
              text='Getting started is easy!'
              size='small'
              className='text-gray-500 mb-4'/>
            <p className='text-gray-700'>{i18n("Completing these three basic steps can quickly get you off and running.")}</p>
            {this.activeSteps.map(this.step)}
            <div className='flex flex-col items-start mt-6'>
              <p className='text-gray-700 mb-3'>{i18n("Take a moment to watch the getting started video.")}</p>
              <Button
                text='Getting Started'
                onClick={this.handleStartVideoClick} />
              <p className='text-gray-700 mt-5 mb-3'>{i18n("Request a walkthrough demo with our team.")}</p>
              <Button
                text='Request a Demo'
                onClick={this.handleDemoClick} />
            </div>
          <Icon
            name='close'
            size='medium'
            color='dark'
            className='absolute right-3 top-3 cursor-pointer'
            onClick={this.handleCloseClick} />
        </Card>
      </div>
  }
}
